/**
 * The firm's clock — how every staff-facing date and time is written.
 * @file src/shared/datetime.ts
 *
 * ── Why a firm setting and not the browser's ───────────────────────────────
 * Dates used to be formatted in whatever zone each viewer's browser was in. A
 * paralegal in Denver and a partner in New York saw the same appointment at
 * different hours — and near midnight on different DAYS, so the timeline,
 * the diary and the notes disagreed depending on who opened them. A firm runs
 * on one clock, its office's. This is that clock.
 *
 * ── Shared with the server ────────────────────────────────────────────────
 * The same zone decides {{date.today}} in a document. Left to the server's
 * own clock, a retainer generated on a US evening carried tomorrow's date
 * whenever the server sat east of the firm.
 *
 * ── Three formats, not a format string ─────────────────────────────────────
 * US, UK and international cover everyone who has asked. A free-form pattern
 * would be a second, worse template language and a support queue.
 *
 * @llm-rule WHEN: Showing a date or time to firm staff, or dating a document
 * @llm-rule AVOID: toLocaleString() on a staff screen — it uses the viewer's
 *   browser zone, which is exactly what this replaced
 * @llm-rule AVOID: Using this on a client-facing page — the signing and
 *   booking pages show the CLIENT's own zone and language on purpose
 */

export const DATE_FORMATS = [
  { value: 'us', label: 'US', example: 'Sep 11, 2026, 4:30 PM' },
  { value: 'uk', label: 'UK', example: '11 Sept 2026, 16:30' },
  { value: 'iso', label: 'International', example: '2026-09-11 16:30' },
] as const;

export type DateFormat = (typeof DATE_FORMATS)[number]['value'];

export const DEFAULT_TIMEZONE = 'America/New_York';
export const DEFAULT_DATE_FORMAT: DateFormat = 'us';

export interface DateTimeConfig {
  timeZone: string;
  format: DateFormat;
}

export const DEFAULT_DATETIME: DateTimeConfig = {
  timeZone: DEFAULT_TIMEZONE,
  format: DEFAULT_DATE_FORMAT,
};

/** US zones first — where most firms using this are — by their everyday names. */
export const US_TIMEZONES = [
  { value: 'America/New_York', label: 'Eastern — New York' },
  { value: 'America/Chicago', label: 'Central — Chicago' },
  { value: 'America/Denver', label: 'Mountain — Denver' },
  { value: 'America/Phoenix', label: 'Arizona — no daylight saving' },
  { value: 'America/Los_Angeles', label: 'Pacific — Los Angeles' },
  { value: 'America/Anchorage', label: 'Alaska — Anchorage' },
  { value: 'Pacific/Honolulu', label: 'Hawaii — Honolulu' },
];

export const isDateFormat = (v: unknown): v is DateFormat => DATE_FORMATS.some((f) => f.value === v);

/** A real IANA zone — asked of Intl itself rather than of a list that goes stale. */
export function isTimeZone(v: unknown): v is string {
  if (typeof v !== 'string' || !v || v.length > 64) return false;
  try {
    new Intl.DateTimeFormat('en-US', { timeZone: v }).format(0);
    return true;
  } catch {
    return false;
  }
}

/* "iso" borrows en-GB for the shapes it has no ISO form for — a weekday, a
 * month heading — because day-before-month and a 24-hour clock is the
 * closer convention for the people who pick it. */
const LOCALE: Record<DateFormat, string> = { us: 'en-US', uk: 'en-GB', iso: 'en-GB' };

type Input = string | number | Date;
const toDate = (v: Input) => (v instanceof Date ? v : new Date(v));

/** The wall-clock pieces of an instant in a zone: zero-padded, 24-hour. */
function wallClock(d: Date, timeZone: string) {
  const p = Object.fromEntries(
    new Intl.DateTimeFormat('en-US', {
      timeZone,
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
      hour: '2-digit',
      minute: '2-digit',
      /* h23, not hour12:false — some engines print midnight as "24:00". */
      hourCycle: 'h23',
    })
      .formatToParts(d)
      .map((x) => [x.type, x.value]),
  );
  return { year: p.year, month: p.month, day: p.day, hour: p.hour, minute: p.minute };
}

/**
 * An instant as the firm writes it.
 *   date      Sep 11, 2026          11 Sept 2026          2026-09-11
 *   datetime  Sep 11, 2026, 4:30 PM 11 Sept 2026, 16:30   2026-09-11 16:30
 *   time      4:30 PM              16:30                16:30
 */
export function formatIn(
  value: Input,
  cfg: DateTimeConfig,
  kind: 'date' | 'datetime' | 'time' = 'date',
): string {
  const d = toDate(value);
  if (Number.isNaN(d.getTime())) return '—';

  if (cfg.format === 'iso') {
    const w = wallClock(d, cfg.timeZone);
    const date = `${w.year}-${w.month}-${w.day}`;
    const time = `${w.hour}:${w.minute}`;
    return kind === 'date' ? date : kind === 'time' ? time : `${date} ${time}`;
  }

  return new Intl.DateTimeFormat(LOCALE[cfg.format], {
    timeZone: cfg.timeZone,
    ...(kind !== 'time' ? ({ year: 'numeric', month: 'short', day: 'numeric' } as const) : {}),
    ...(kind !== 'date' ? ({ hour: 'numeric', minute: '2-digit', hour12: cfg.format === 'us' } as const) : {}),
  }).format(d);
}

/** Any other shape — a weekday, a month heading — in the firm's zone and convention. */
export function formatWithIn(value: Input, cfg: DateTimeConfig, opts: Intl.DateTimeFormatOptions): string {
  const d = toDate(value);
  if (Number.isNaN(d.getTime())) return '—';
  const clock = opts.hour !== undefined ? { hour12: cfg.format === 'us' } : {};
  return new Intl.DateTimeFormat(LOCALE[cfg.format], { ...opts, ...clock, timeZone: cfg.timeZone }).format(d);
}

/**
 * "2026-09-11" — which calendar day an instant falls on in a zone. For
 * grouping and comparing, never for display: two appointments are "the same
 * day" when they are the same day at the firm, whatever the viewer's browser
 * thinks.
 */
export function dayKeyIn(value: Input, timeZone: string): string {
  const w = wallClock(toDate(value), timeZone);
  return `${w.year}-${w.month}-${w.day}`;
}
